import { X } from 'lucide-react';
import { useSyncExternalStore, type ReactNode } from 'react';
import { cn } from './cn';
import { RoadSign, type SignName } from './signs';

export interface ToastInput {
  message: ReactNode;
  /** The sign before the message; go for a done job, hazard for a failure. */
  sign?: SignName;
  /** One action, in caution amber. */
  action?: { label: string; onClick: () => void };
  /** ms; 4 s by default, longer for failures. */
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const listener of listeners) listener();
}

export function dismissToast(id: number) {
  clearTimeout(timers.get(id));
  timers.delete(id);
  items = items.filter((t) => t.id !== id);
  emit();
}

/** Shows a toast; the newest at the top. Returns its id. */
export function toast(input: ToastInput | string): number {
  const item: ToastItem = { sign: 'go', ...(typeof input === 'string' ? { message: input } : input), id: nextId++ };
  items = [item, ...items].slice(0, 4);
  timers.set(
    item.id,
    setTimeout(() => dismissToast(item.id), item.duration ?? (item.sign === 'hazard' ? 7000 : 4000)),
  );
  emit();
  return item.id;
}

toast.error = (message: ReactNode) => toast({ message, sign: 'hazard' });
toast.info = (message: ReactNode) => toast({ message, sign: 'info' });

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => items;

/** Toasts sit at the top end on the web (`className` moves them below an app's top bar). Surface-inverse, radius 8. */
export function Toaster({ dismissLabel, className }: { dismissLabel: string; className?: string }) {
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (
    <div className={cn('pointer-events-none fixed end-4 top-4 z-toast grid w-[min(440px,calc(100vw-32px))] gap-2', className)}>
      {list.map((t) => (
        <div
          key={t.id}
          role={t.sign === 'hazard' ? 'alert' : 'status'}
          className="pointer-events-auto flex items-center gap-3 rounded-md bg-surface-inverse py-3 pe-2 ps-4 text-[14px] leading-5 text-ink-inverse shadow-3 animate-arrive"
        >
          {t.sign && <RoadSign sign={t.sign} size={18} />}
          <span className="min-w-0 flex-1">{t.message}</span>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action?.onClick();
                dismissToast(t.id);
              }}
              className="shrink-0 rounded-sm bg-caution-fill px-2 py-1 text-[12px] font-extrabold uppercase tracking-[0.04em] text-caution-on-fill"
            >
              {t.action.label}
            </button>
          )}
          <button
            type="button"
            aria-label={dismissLabel}
            onClick={() => dismissToast(t.id)}
            className="grid size-7 shrink-0 place-items-center rounded-sm opacity-70 hover:opacity-100"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}
